import { useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { Search } from 'lucide-react';
import type { WarehouseCard } from '@/apis/warehouse';
import { useWarehouseKanban, useWarehouseHandlers } from '@/hooks/useWarehouse';
import { useSalespersonsQuery } from '@/hooks/useSalespersons';
import { NativeSelect } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { Empty } from '@/components/ui/empty';
import { KANBAN_COLUMNS, resolveClassification } from './warehouse-shared';
import { OrderClassification } from '@/apis/warehouse';
import { KanbanColumn } from './KanbanColumn';
import { OrderDetailDrawer } from './OrderDetailDrawer';

const CLASSIFICATION_OPTIONS = ['All', 'Domestic', 'International'];
const ALL = 'All';

export function WarehousePage() {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebounceValue(searchInput, 400);
  const [classification, setClassification] = useState(ALL);
  const [salesFilter, setSalesFilter] = useState(ALL);
  const [handlerFilter, setHandlerFilter] = useState(ALL);
  const [activeOrderId, setActiveOrderId] = useState<string | null>(null);

  const { data: board, isLoading, isError } = useWarehouseKanban();
  const { data: salespersons = [] } = useSalespersonsQuery();
  const { data: handlers = [] } = useWarehouseHandlers();

  const salesOptions = [ALL, ...salespersons.map((sp) => `${sp.firstName} ${sp.lastName}`)];
  const handlerOptions = [ALL, ...handlers.map((h) => `${h.firstName} ${h.lastName}`)];

  const matchesFilters = (card: WarehouseCard) => {
    if (debouncedSearch && !card.orderNumber?.toLowerCase().includes(debouncedSearch.toLowerCase())) {
      return false;
    }
    if (
      classification !== ALL &&
      resolveClassification(card.orderClassification, card.destinationCountry) !==
        (classification.toLowerCase() as OrderClassification)
    ) {
      return false;
    }
    if (salesFilter !== ALL && card.assignedSalesperson?.name !== salesFilter) {
      return false;
    }
    if (handlerFilter !== ALL && card.assignedWarehouseHandler?.name !== handlerFilter) {
      return false;
    }
    return true;
  };

  const visibleColumns = useMemo(() => {
    if (!board) return [];
    return KANBAN_COLUMNS.filter((status) => board[status] !== undefined);
  }, [board]);

  const totalCards = visibleColumns.reduce((sum, status) => sum + (board?.[status]?.length ?? 0), 0);

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-black">Warehouse Management</h1>
          <p className="text-base font-normal text-lightGray">
            Real-time package fulfillment, logistics tracking, and order assignment console.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-3">
          <div className="border-smoke bg-neutral-white flex h-11 w-64 items-center gap-2 rounded-xl border px-3">
            <Search size={16} className="text-lightGray" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search Order Number"
              className="h-full w-full border-0 bg-transparent text-base text-black placeholder:text-lightGray outline-none"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-normal text-lightGray">Classification</label>
            <div className="w-40">
              <NativeSelect value={classification} onChange={setClassification} options={CLASSIFICATION_OPTIONS} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-normal text-lightGray">Sale</label>
            <div className="w-40">
              <NativeSelect value={salesFilter} onChange={setSalesFilter} options={salesOptions} />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-normal text-lightGray">Handler</label>
            <div className="w-40">
              <NativeSelect value={handlerFilter} onChange={setHandlerFilter} options={handlerOptions} />
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex gap-3 overflow-x-auto">
          {KANBAN_COLUMNS.map((status) => (
            <Skeleton key={status} className="h-[600px] w-[264px] shrink-0 rounded-xl" />
          ))}
        </div>
      ) : isError ? (
        <Empty description="Couldn't load the warehouse board. You may not have permission to view it." />
      ) : totalCards === 0 ? (
        <Empty description="No warehouse orders found" />
      ) : (
        <div className="flex flex-1 gap-3 overflow-x-auto pb-2">
          {visibleColumns.map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              cards={(board?.[status] ?? []).filter(matchesFilters)}
              onOpenDetails={setActiveOrderId}
            />
          ))}
        </div>
      )}

      <OrderDetailDrawer orderId={activeOrderId} onClose={() => setActiveOrderId(null)} />
    </div>
  );
}
